// ---------------------------------------------------------------------------
// Virtue Health — Account Map data model (real HubSpot data)
// ---------------------------------------------------------------------------
// TIERS mirror HubSpot Lifecycle Stage:
//   t1 = Customer            (active revenue)
//   t2 = Opportunity/Quoting (in active quoting motion)
//   t3 = SQL                 (sales-qualified lead)
//   t4 = MQL                 (marketing-qualified lead)
//   t5 = No Traction         (target account, no engagement yet)
// Territories map to HubSpot ownership regions.
// ---------------------------------------------------------------------------

export const TIERS = {
  t1: {
    key: "t1",
    label: "Customer",
    short: "T1",
    ordinal: 1,
    color: "#10B981",
    glow: "rgba(16, 185, 129, 0.7)",
    desc: "Customer — active revenue.",
  },
  t2: {
    key: "t2",
    label: "Quoting",
    short: "T2",
    ordinal: 2,
    color: "#EAB308",
    glow: "rgba(234, 179, 8, 0.65)",
    desc: "Opportunity — actively quoting.",
  },
  t3: {
    key: "t3",
    label: "SQL",
    short: "T3",
    ordinal: 3,
    color: "#22D3EE",
    glow: "rgba(34, 211, 238, 0.65)",
    desc: "Sales-qualified lead.",
  },
  t4: {
    key: "t4",
    label: "MQL",
    short: "T4",
    ordinal: 4,
    color: "#A78BFA",
    glow: "rgba(167, 139, 250, 0.65)",
    desc: "Marketing-qualified lead.",
  },
  t5: {
    key: "t5",
    label: "Lead",
    short: "T5",
    ordinal: 5,
    color: "#71717A",
    glow: "rgba(113, 113, 122, 0.55)",
    desc: "Lead — in the funnel, no qualification yet.",
  },
  // Special tier reserved for the Virtue Health sales team (not surfaced in
  // the T1-T5 filter grid). Rendered on the map with a distinctive
  // reddish-brown node so they never get confused with the tier palette.
  sales: {
    key: "sales",
    label: "Sales Team",
    short: "VH",
    ordinal: 0,
    color: "#B85450",
    glow: "rgba(184, 84, 80, 0.7)",
    desc: "Virtue Health sales team member.",
  },
  // TPA points of contact — distinct indigo palette so they never get
  // confused with the CRM tier ladder or sales team markers.
  tpa: {
    key: "tpa",
    label: "TPA Contact",
    short: "TPA",
    ordinal: -1,
    color: "#818CF8",
    glow: "rgba(129, 140, 248, 0.65)",
    desc: "Point of contact at a Third-Party Administrator.",
  },
  // Employers / HR leaders — outside the broker ladder; markers are coloured
  // by Industry, so this entry only supplies the fallback label/amber accent.
  employer: {
    key: "employer",
    label: "Employer",
    short: "EMP",
    ordinal: -2,
    color: "#F59E0B",
    glow: "rgba(245, 158, 11, 0.65)",
    desc: "Employer / HR leader.",
  },
};

export const TIER_ORDER = ["t1", "t2", "t3", "t4", "t5"];
export const SALES_TIER = "sales";
export const SALES_TEAM_KIND = "sales_team";
export const TPA_CONTACT_KIND = "tpa_contact";

export const LIFECYCLE_OPTIONS = [
  { value: "Customer", tier: "t1" },
  { value: "Opportunity (Quoting)", tier: "t2" },
  { value: "SQL (Sales Qualified Lead)", tier: "t3" },
  { value: "Marketing Qualified Lead", tier: "t4" },
  { value: "", tier: "t5" },
];

export const TERRITORIES = {
  northeast: { key: "northeast", label: "Northeast", short: "NE" },
  midwest: { key: "midwest", label: "Midwest", short: "MW" },
  southeast: { key: "southeast", label: "Southeast", short: "SE" },
  south: { key: "south", label: "South", short: "S" },
};

export const TERRITORY_ORDER = ["northeast", "midwest", "southeast", "south"];

// Full state name lookup for tooltips (2-letter -> readable).
export const STATE_NAME = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan",
  MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

export function tierFromLifecycle(raw) {
  const v = (raw || "").trim().toLowerCase();
  if (!v) return "t5";
  if (v === "customer") return "t1";
  if (v.startsWith("opportunity") || v === "quoting") return "t2";
  if (v.startsWith("sql") || v === "sales qualified lead") return "t3";
  if (v === "marketing qualified lead" || v === "mql") return "t4";
  return "t5";
}
